import { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

function MapCleanup() {
  const map = useMap();
  const mapRef = useRef(map);
  mapRef.current = map;

  useEffect(() => {
    return () => {
      try {
        mapRef.current?.remove();
      } catch (e) {}
    };
  }, []);

  return null;
}

// Fix Leaflet default icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

function RecenterMap({ lat, lng }) {
  const map = useMap();
  const prevRef = useRef({ lat: null, lng: null });

  useEffect(() => {
    if (prevRef.current.lat !== lat || prevRef.current.lng !== lng) {
      prevRef.current = { lat, lng };
      map.setView([lat, lng], 15);
    }
  }, [lat, lng]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

function ClickHandler({ onLocationChange, readOnly }) {
  useMapEvents({
    click(e) {
      if (!readOnly && onLocationChange) {
        onLocationChange(e.latlng.lat, e.latlng.lng);
      }
    },
  });
  return null;
}

function DraggableMarker({ lat, lng, readOnly, onLocationChange }) {
  const eventHandlers =
    !readOnly && onLocationChange
      ? {
          dragend(e) {
            const marker = e.target;
            const pos = marker.getLatLng();
            onLocationChange(pos.lat, pos.lng);
          },
        }
      : {};

  return <Marker position={[lat, lng]} draggable={!readOnly} eventHandlers={eventHandlers} />;
}

function MapaUbicacion({ lat, lng, readOnly = false, onLocationChange = null, height = '200px' }) {
  if (!lat || !lng) {
    return (
      <div
        style={{
          width: '100%',
          height,
          backgroundColor: 'var(--agx-skeleton)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '8px',
          color: 'var(--agx-muted)',
          fontSize: '13px',
          border: '1px solid var(--agx-border)',
        }}
      >
        Sin ubicación registrada
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        height,
        borderRadius: '8px',
        overflow: 'hidden',
        border: '1px solid var(--agx-border)',
      }}
    >
      <MapContainer
        center={[lat, lng]}
        zoom={15}
        style={{ width: '100%', height: '100%' }}
        zoomControl={true}
        scrollWheelZoom={!readOnly}
        dragging={!readOnly}
        doubleClickZoom={!readOnly}
      >
        <MapCleanup />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <RecenterMap lat={lat} lng={lng} />
        <DraggableMarker
          lat={lat}
          lng={lng}
          readOnly={readOnly}
          onLocationChange={onLocationChange}
        />
        <ClickHandler onLocationChange={onLocationChange} readOnly={readOnly} />
      </MapContainer>
      {readOnly && (
        <div
          style={{
            fontSize: '11px',
            color: 'var(--agx-muted)',
            padding: '2px 6px',
            backgroundColor: 'var(--agx-surface)',
            textAlign: 'center',
          }}
        >
          {lat.toFixed(6)}, {lng.toFixed(6)}
        </div>
      )}
    </div>
  );
}

export default MapaUbicacion;
